/**
 * Save-set orchestration: new set → create; overwrite an UNUSED set → plain
 * update; overwrite a set in use → the ask-on-edit fork prompt ("Update for
 * everyone" vs "Save as copy", the copy optionally repointing the loaded
 * package). Usage counts are server-truth — never optimistic.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState } from 'react';

import { useToast } from '@/providers/ToastProvider';

import { useLibrarySets, useSetMutations } from '../api/hooks/useLibrarySets';
import { useLibraryUsage } from '../api/hooks/useLibraryUsage';
import type {
  LibrarySetKind,
  LibrarySetWriteBody,
} from '../api/client/sets-api';

export interface ForkPrompt {
  setId: string;
  setName: string;
  packages: number;
  campaigns: number;
  pending: LibrarySetWriteBody;
}

export interface SaveSetInput {
  name: string;
  description?: string;
  overwriteId?: string;
}

export function useSaveSet(opts: {
  kind: LibrarySetKind;
  buildPayload: () => LibrarySetWriteBody;
  /** Draft's loaded package — "Save as copy" repoints it at the copy. */
  packageId?: string | null;
}) {
  const toast = useToast();
  const sets = useLibrarySets(opts.kind);
  const usage = useLibraryUsage();
  const mutations = useSetMutations(opts.kind);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [forkPrompt, setForkPrompt] = useState<ForkPrompt | null>(null);

  const fail = (error: unknown) => {
    const detail = error instanceof Error ? error.message : 'Try again.';
    toast.error('Save failed', detail);
  };

  const submit = (input: SaveSetInput) => {
    const payload = opts.buildPayload();
    if (!input.overwriteId) {
      mutations.create.mutate(
        { ...payload, name: input.name, description: input.description },
        {
          onSuccess: (set) => {
            setDialogOpen(false);
            toast.success('Set saved', `"${set.name}" is in the library.`);
          },
          onError: fail,
        },
      );
      return;
    }
    const target = (sets.data ?? []).find((s) => s.id === input.overwriteId);
    if (!target) return;
    const bucket =
      opts.kind === 'formula' ? usage.data?.formulaSets : usage.data?.ruleSets;
    const counts = bucket?.[target.id] ?? { packages: 0, campaigns: 0 };
    if (counts.packages > 0 || counts.campaigns > 0) {
      setForkPrompt({
        setId: target.id,
        setName: target.name,
        packages: counts.packages,
        campaigns: counts.campaigns,
        pending: payload,
      });
      return;
    }
    mutations.update.mutate(
      { id: target.id, body: payload },
      {
        onSuccess: () => {
          setDialogOpen(false);
          toast.success('Set updated', `"${target.name}" now holds this work.`);
        },
        onError: fail,
      },
    );
  };

  const updateForEveryone = () => {
    if (!forkPrompt) return;
    mutations.update.mutate(
      { id: forkPrompt.setId, body: forkPrompt.pending },
      {
        onSuccess: () => {
          closeAll();
          const detail = 'Campaigns using it load the new version from now on.';
          toast.success('Set updated for everyone', detail);
        },
        onError: fail,
      },
    );
  };

  const saveAsCopy = () => {
    if (!forkPrompt) return;
    mutations.fork.mutate(
      {
        id: forkPrompt.setId,
        body: {
          ...forkPrompt.pending,
          ...(opts.packageId ? { packageId: opts.packageId } : {}),
        },
      },
      {
        onSuccess: (copy) => {
          closeAll();
          const repointed = opts.packageId ? ', loaded package repointed' : '';
          toast.success('Copy created', `"${copy.name}" saved${repointed}.`);
        },
        onError: fail,
      },
    );
  };

  const closeAll = () => {
    setForkPrompt(null);
    setDialogOpen(false);
  };

  const saving = [mutations.create, mutations.update, mutations.fork].some(
    (m) => m.isPending,
  );

  return {
    sets: sets.data ?? [],
    saving,
    dialogOpen,
    setDialogOpen,
    forkPrompt,
    cancelFork: () => setForkPrompt(null),
    submit,
    updateForEveryone,
    saveAsCopy,
  };
}
